import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { useApp } from "../context/AppContext";
import { loadCanvasImage } from "../lib/imageLoader";
import {
  MoveUpRight, Circle, Square, Type, Pen, Droplets, Hash, Undo2, X, Check, ZoomIn, ZoomOut, RotateCcw, Hand,
} from "lucide-react";

import { normalizeSpokenMeasure } from "../lib/spokenNumbers";

const COLORS = ["#DC2626", "#F59E0B", "#0E7490", "#111827", "#FFFFFF"];
export const TEXT_LINE_HEIGHT = 1.2;

/** Max text width: from the anchor to the right edge, never narrower than 30% of the image. */
export const textMaxWidth = (x, width) => Math.max(width * 0.3, width * 0.98 - x);
/** Anchor pulled left when needed so the wrapped block still ends inside the photo. */
export const textAnchorX = (x, width) => Math.min(x, width * 0.98 - textMaxWidth(x, width));

/** Word-wrap (breaking over-long words) so a text never runs past the photo edge. */
export function wrapText(ctx, text, maxWidth) {
  const lines = [];
  for (const para of String(text).split("\n")) {
    let line = "";
    for (const word of para.split(" ")) {
      let w = word;
      while (ctx.measureText(w).width > maxWidth && w.length > 1) {
        let cut = w.length - 1;
        while (cut > 1 && ctx.measureText(w.slice(0, cut)).width > maxWidth) cut--;
        if (line) { lines.push(line); line = ""; }
        lines.push(w.slice(0, cut));
        w = w.slice(cut);
      }
      const test = line ? `${line} ${w}` : w;
      if (line && ctx.measureText(test).width > maxWidth) { lines.push(line); line = w; } else line = test;
    }
    lines.push(line);
  }
  return lines;
}

export function MarkupEditor({ open, imageSrc, onSave, onClose, initialView }) {
  const { t } = useApp();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  // No drawing tool pre-selected: open in "move" mode so the user can position
  // the image first without accidentally drawing strokes.
  const [tool, setTool] = useState("move");
  const [color, setColor] = useState(COLORS[0]);
  const [actions, setActions] = useState([]);
  const [drawing, setDrawing] = useState(null);
  const [textInput, setTextInput] = useState(null); // {x, y, value}
  const [numberCounter, setNumberCounter] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [dragText, setDragText] = useState(null); // {index, dx, dy} — moving a placed text

  // ---- Pinch/wheel zoom of the working canvas (2 fingers = zoom/pan, 1 finger = draw) ----
  const [view, setView] = useState({ z: 1, x: 0, y: 0 });
  const viewRef = useRef({ z: 1, x: 0, y: 0 });
  viewRef.current = view;
  const pinchRef = useRef(null); // {startDist, startZoom, startX, startY, startPan}
  const pinchTextRef = useRef(null); // pinch ON a held text = resize the text (not the canvas)
  const panDragRef = useRef(null); // {sx, sy, start} — single-finger pan in "move" mode
  const kbInputRef = useRef(null); // hidden input focused synchronously in the touch gesture so the mobile keyboard opens
  const lastTouchTsRef = useRef(0); // guards against emulated mouse events fired ~300ms after a touch (ghost clicks)
  const clampMZ = (z) => Math.min(5, Math.max(1, z));
  const resetView = useCallback(() => { setView({ z: 1, x: 0, y: 0 }); pinchRef.current = null; }, []);
  // iOS/Android only open the keyboard if focus() runs INSIDE the user gesture —
  // focus this hidden input first, then the real text input inherits the keyboard.
  const kbFocus = () => { try { kbInputRef.current?.focus({ preventScroll: true }); } catch (err) { /* ignore */ } };

  // Close on Escape (unless a text overlay is being edited — its input handles Escape itself)
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !textInput) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, textInput, onClose]);

  // Load image
  useEffect(() => {
    if (!open || !imageSrc) return;
    setActions([]);
    setNumberCounter(1);
    setImgLoaded(false);
    setTextInput(null);
    setTool("move");
    // Inherit the zoom/pan the user had in the PhotoViewer so markup opens on the same framing
    if (initialView && initialView.z && initialView.z > 1.02) {
      setView({ z: clampMZ(initialView.z), x: initialView.x || 0, y: initialView.y || 0 });
      pinchRef.current = null;
    } else {
      resetView();
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      setImgLoaded(true);
    };
    img.onerror = () => {
      // retry without CORS (won't be able to export, but at least visible)
      const img2 = new Image();
      img2.onload = () => { imgRef.current = img2; setImgLoaded(true); };
      img2.src = imageSrc;
    };
    // Canvas-safe route first (blob) — required in the native app, where
    // cross-origin images taint the canvas and break "Save" (toDataURL).
    loadCanvasImage(imageSrc)
      .then((safeImg) => { imgRef.current = safeImg; setImgLoaded(true); })
      .catch(() => { img.src = imageSrc; });
  }, [open, imageSrc]);

  const paintOn = useCallback((canvas, img, list) => {
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.drawImage(img, 0, 0);
    const strokeW = Math.max(4, Math.round(img.naturalWidth / 250));
    const fontSize = Math.max(20, Math.round(img.naturalWidth / 40));

    const drawAction = (a) => {
      ctx.strokeStyle = a.color;
      ctx.fillStyle = a.color;
      ctx.lineWidth = strokeW;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (a.tool === "pen" && a.points.length > 1) {
        ctx.beginPath();
        ctx.moveTo(a.points[0].x, a.points[0].y);
        a.points.forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.stroke();
      } else if (a.tool === "arrow") {
        const { start, end } = a;
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const headLen = strokeW * 4;
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(end.x - headLen * Math.cos(angle - Math.PI / 6), end.y - headLen * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(end.x - headLen * Math.cos(angle + Math.PI / 6), end.y - headLen * Math.sin(angle + Math.PI / 6));
        ctx.stroke();
      } else if (a.tool === "circle") {
        const { start, end } = a;
        ctx.beginPath();
        ctx.ellipse((start.x + end.x) / 2, (start.y + end.y) / 2, Math.abs(end.x - start.x) / 2, Math.abs(end.y - start.y) / 2, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else if (a.tool === "rect") {
        const { start, end } = a;
        ctx.strokeRect(Math.min(start.x, end.x), Math.min(start.y, end.y), Math.abs(end.x - start.x), Math.abs(end.y - start.y));
      } else if (a.tool === "blur") {
        const { start, end } = a;
        const x = Math.min(start.x, end.x);
        const y = Math.min(start.y, end.y);
        const w = Math.abs(end.x - start.x);
        const h = Math.abs(end.y - start.y);
        if (w > 4 && h > 4) {
          const scale = 0.04;
          const tmp = document.createElement("canvas");
          tmp.width = Math.max(1, Math.round(w * scale));
          tmp.height = Math.max(1, Math.round(h * scale));
          const tctx = tmp.getContext("2d");
          tctx.imageSmoothingEnabled = true;
          try {
            tctx.drawImage(canvas, x, y, w, h, 0, 0, tmp.width, tmp.height);
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(tmp, 0, 0, tmp.width, tmp.height, x, y, w, h);
            ctx.imageSmoothingEnabled = true;
          } catch (e) { /* tainted canvas */ }
        }
      } else if (a.tool === "text") {
        const fs = Math.round(fontSize * (a.size || 1));
        ctx.font = `600 ${fs}px 'IBM Plex Sans', sans-serif`;
        ctx.lineWidth = Math.max(3, strokeW - 2);
        ctx.strokeStyle = a.color === "#FFFFFF" ? "#111827" : "#FFFFFF";
        const ax = textAnchorX(a.x, canvas.width);
        wrapText(ctx, a.text, textMaxWidth(a.x, canvas.width)).forEach((ln, li) => {
          const y = a.y + li * fs * TEXT_LINE_HEIGHT;
          ctx.strokeText(ln, ax, y);
          ctx.fillText(ln, ax, y);
        });
      } else if (a.tool === "number") {
        const r = fontSize * 0.9;
        ctx.beginPath();
        ctx.arc(a.x, a.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = a.color === "#FFFFFF" ? "#111827" : "#FFFFFF";
        ctx.font = `700 ${fontSize}px 'IBM Plex Sans', sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(a.n), a.x, a.y);
        ctx.textAlign = "start";
        ctx.textBaseline = "alphabetic";
      }
    };

    list.forEach(drawAction);
  }, []);

  const redraw = useCallback(() => {
    paintOn(canvasRef.current, imgRef.current, drawing ? [...actions, drawing] : actions);
  }, [actions, drawing, paintOn]);

  useEffect(() => { if (imgLoaded) redraw(); }, [imgLoaded, redraw]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const onPointerDown = (e) => {
    // A single tap on mobile fires touchstart AND an emulated mousedown right after —
    // ignore the mouse ghost so tools like "#" place exactly ONE item per tap.
    if (e.touches) lastTouchTsRef.current = Date.now();
    else if (Date.now() - lastTouchTsRef.current < 800) return;
    if (textInput) return;
    // Multi-touch belongs to the pinch-zoom gesture, never to drawing
    if (e.touches && e.touches.length > 1) return;
    if (pinchRef.current || pinchTextRef.current) return;
    const pos = getPos(e);
    // Placed texts are movable objects: grab one if the press starts on it
    const hit = hitTextIndex(pos);
    if (hit >= 0) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setDragText({
        index: hit,
        dx: pos.x - actions[hit].x,
        dy: pos.y - actions[hit].y,
        clientX, clientY, t: Date.now(), moved: false,
      });
      return;
    }
    if (tool === "move") {
      // One-finger / mouse drag repositions the image instead of drawing
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      panDragRef.current = { sx: clientX, sy: clientY, start: { x: viewRef.current.x, y: viewRef.current.y } };
      return;
    }
    if (tool === "text") {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      kbFocus(); // open the mobile keyboard within this gesture
      setTextInput({ x: pos.x, y: pos.y, screenX: clientX - rect.left, screenY: clientY - rect.top, value: "" });
      return;
    }
    if (tool === "number") {
      setActions((prev) => [...prev, { tool: "number", color, x: pos.x, y: pos.y, n: numberCounter }]);
      setNumberCounter((n) => n + 1);
      return;
    }
    if (tool === "pen") {
      setDrawing({ tool: "pen", color, points: [pos] });
    } else {
      setDrawing({ tool, color, start: pos, end: pos });
    }
  };

  // Hit-test placed text annotations (topmost first) so they can be dragged
  const hitTextIndex = (pos) => {
    const canvas = canvasRef.current;
    if (!canvas) return -1;
    const ctx = canvas.getContext("2d");
    const baseSize = Math.max(20, Math.round(canvas.width / 40));
    for (let i = actions.length - 1; i >= 0; i--) {
      const a = actions[i];
      if (a.tool !== "text") continue;
      const fs = baseSize * (a.size || 1);
      ctx.font = `600 ${Math.round(fs)}px 'IBM Plex Sans', sans-serif`;
      const lines = wrapText(ctx, a.text, textMaxWidth(a.x, canvas.width));
      const ax = textAnchorX(a.x, canvas.width);
      const w = Math.max(...lines.map((ln) => ctx.measureText(ln).width));
      const h = fs + (lines.length - 1) * fs * TEXT_LINE_HEIGHT;
      const pad = fs * 0.4;
      if (pos.x >= ax - pad && pos.x <= ax + w + pad && pos.y >= a.y - fs - pad && pos.y <= a.y - fs + h + pad) {
        return i;
      }
    }
    return -1;
  };

  const onPointerMove = (e) => {
    if (pinchRef.current || pinchTextRef.current || (e.touches && e.touches.length > 1)) return;
    if (panDragRef.current) {
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const pd = panDragRef.current;
      setView((v) => ({ ...v, x: pd.start.x + (clientX - pd.sx), y: pd.start.y + (clientY - pd.sy) }));
      return;
    }
    if (dragText) {
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const movedNow = dragText.moved || Math.hypot(clientX - dragText.clientX, clientY - dragText.clientY) > 8;
      if (movedNow && !dragText.moved) setDragText((d) => ({ ...d, moved: true }));
      if (!movedNow) return; // still a tap, don't move yet
      const pos = getPos(e);
      setActions((prev) => prev.map((a, i) =>
        i === dragText.index ? { ...a, x: pos.x - dragText.dx, y: pos.y - dragText.dy } : a));
      return;
    }
    if (!drawing) return;
    e.preventDefault();
    const pos = getPos(e);
    setDrawing((d) => (d.tool === "pen" ? { ...d, points: [...d.points, pos] } : { ...d, end: pos }));
  };

  const onPointerUp = () => {
    if (pinchRef.current || pinchTextRef.current) return;
    if (panDragRef.current) {
      panDragRef.current = null;
      return;
    }
    if (dragText) {
      const d = dragText;
      setDragText(null);
      // Quick tap on a placed text (no drag) -> reopen its editor
      if (!d.moved && Date.now() - d.t < 500) {
        const a = actions[d.index];
        if (a) {
          kbFocus(); // open the mobile keyboard within this gesture
          const canvas = canvasRef.current;
          const rect = canvas.getBoundingClientRect();
          setTextInput({
            x: a.x, y: a.y,
            screenX: d.clientX - rect.left, screenY: d.clientY - rect.top,
            value: a.text, color: a.color, size: a.size || 1, original: a,
          });
          setActions((prev) => prev.filter((_, i) => i !== d.index));
        }
      }
      return;
    }
    if (!drawing) return;
    setActions((prev) => [...prev, drawing]);
    setDrawing(null);
  };

  const commitText = () => {
    if (textInput && textInput.value.trim()) {
      setActions((prev) => [...prev, {
        tool: "text",
        color: textInput.color || color,
        size: textInput.size || 1,
        x: textInput.x, y: textInput.y,
        text: normalizeSpokenMeasure(textInput.value.trim()), // dictated "quarenta e quatro três quartos" → 44 3/4
      }]);
    }
    setTextInput(null);
  };

  // Cancel editing: put the original text back untouched
  const cancelTextEdit = () => {
    if (textInput?.original) setActions((prev) => [...prev, textInput.original]);
    setTextInput(null);
  };

  const undo = () => {
    setActions((prev) => {
      const next = prev.slice(0, -1);
      const last = prev[prev.length - 1];
      if (last?.tool === "number") setNumberCounter((n) => Math.max(1, n - 1));
      return next;
    });
  };

  const save = () => {
    try {
      const canvas = canvasRef.current;
      // Export WITHOUT the text layer — texts are sent as movable, translatable overlays
      const nonText = actions.filter((a) => a.tool !== "text");
      paintOn(canvas, imgRef.current, nonText);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      const overlays = actions.filter((a) => a.tool === "text").map((a) => ({
        text: a.text, x: a.x / canvas.width, y: a.y / canvas.height, color: a.color, size: a.size || 1,
      }));
      redraw(); // restore the on-screen preview (with texts)
      onSave(dataUrl, overlays);
    } catch (e) {
      // tainted canvas fallback
      onClose();
    }
  };

  if (!open) return null;

  // ---- Container-level pinch/zoom gestures ----
  const mDist = (e) => {
    const [a, b] = [e.touches[0], e.touches[1]];
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };
  const mMid = (e) => ({
    x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
    y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
  });
  const containerTouchStart = (e) => {
    if (e.touches.length === 2) {
      // A second finger cancels any in-progress stroke and starts the zoom gesture
      setDrawing(null);
      panDragRef.current = null;
      if (dragText) {
        // Pinch while HOLDING a text = resize that text (font size), not the canvas
        pinchTextRef.current = {
          index: dragText.index,
          startDist: mDist(e),
          startSize: (actions[dragText.index]?.size) || 1,
        };
        setDragText(null);
        return;
      }
      setDragText(null);
      const mid = mMid(e);
      pinchRef.current = {
        startDist: mDist(e), startZoom: viewRef.current.z,
        startMid: mid, startPan: { x: viewRef.current.x, y: viewRef.current.y },
      };
    }
  };
  const containerTouchMove = (e) => {
    const pt = pinchTextRef.current;
    if (pt && e.touches.length === 2) {
      // Live-resize the held text between 0.4x and 5x
      const next = Math.min(5, Math.max(0.4, pt.startSize * (mDist(e) / pt.startDist)));
      setActions((prev) => prev.map((a, i) => (i === pt.index ? { ...a, size: next } : a)));
      return;
    }
    const g = pinchRef.current;
    if (g && e.touches.length === 2) {
      const mid = mMid(e);
      setView({
        z: clampMZ(g.startZoom * (mDist(e) / g.startDist)),
        x: g.startPan.x + (mid.x - g.startMid.x),
        y: g.startPan.y + (mid.y - g.startMid.y),
      });
    }
  };
  const containerTouchEnd = (e) => {
    if (e.touches.length < 2) {
      pinchTextRef.current = null;
      if (pinchRef.current) {
        pinchRef.current = null;
        if (viewRef.current.z <= 1.02) resetView();
      }
    }
  };
  const containerWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    setView((v) => ({ ...v, z: clampMZ(v.z - Math.sign(e.deltaY) * 0.3) }));
  };

  const tools = [
    { key: "move", icon: Hand },
    { key: "pen", icon: Pen },
    { key: "arrow", icon: MoveUpRight },
    { key: "circle", icon: Circle },
    { key: "rect", icon: Square },
    { key: "text", icon: Type },
    { key: "blur", icon: Droplets },
    { key: "number", icon: Hash },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[300] bg-black/95 flex flex-col pt-safe" style={{ pointerEvents: "auto" }} data-testid="photo-markup-editor">
      {/* Hidden input: focused synchronously inside touch gestures so the mobile keyboard opens instantly */}
      <input
        ref={kbInputRef}
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: "fixed", top: 0, left: 0, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }}
      />
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-black/60">
        <Button variant="ghost" size="icon" data-testid="markup-cancel" onClick={onClose} className="text-white hover:bg-white/10 h-11 w-11">
          <X className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" data-testid="markup-zoom-out" onClick={() => setView((v) => ({ ...v, z: clampMZ(v.z - 0.5) }))} className="text-white hover:bg-white/10 h-11 w-11">
            <ZoomOut className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" data-testid="markup-zoom-in" onClick={() => setView((v) => ({ ...v, z: clampMZ(v.z + 0.5) }))} className="text-white hover:bg-white/10 h-11 w-11">
            <ZoomIn className="h-5 w-5" />
          </Button>
          {view.z !== 1 && (
            <Button variant="ghost" size="icon" data-testid="markup-zoom-reset" onClick={resetView} className="text-white hover:bg-white/10 h-11 w-11">
              <RotateCcw className="h-5 w-5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" data-testid="markup-undo" onClick={undo} disabled={actions.length === 0} className="text-white hover:bg-white/10 h-11 w-11">
            <Undo2 className="h-5 w-5" />
          </Button>
          <Button data-testid="markup-save-version" onClick={save} className="bg-primary text-primary-foreground h-10 px-4 ml-1">
            <Check className="h-4 w-4 mr-1.5" /> {t("save")}
          </Button>
        </div>
      </div>

      {/* Canvas area (pinch with 2 fingers to zoom while drawing details) */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden relative p-2"
        onTouchStart={containerTouchStart}
        onTouchMove={containerTouchMove}
        onTouchEnd={containerTouchEnd}
        onWheel={containerWheel}
        style={{ touchAction: "none" }}
      >
        {!imgLoaded && <div className="text-white/60 text-sm">...</div>}
        <canvas
          ref={canvasRef}
          className="markup-canvas object-contain rounded"
          style={{
            display: imgLoaded ? "block" : "none",
            maxWidth: "100%",
            maxHeight: "100%",
            cursor: tool === "move" ? "grab" : "crosshair",
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})`,
            transformOrigin: "center center",
            transition: pinchRef.current ? "none" : "transform 120ms ease-out",
          }}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onMouseLeave={onPointerUp}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
        />
        {textInput && (
          <div
            className="absolute"
            style={{ left: Math.min(textInput.screenX + 8, (containerRef.current?.clientWidth || 300) - 220), top: textInput.screenY + 8 }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1 bg-black/80 rounded-lg p-1.5">
              <input
                autoFocus
                data-testid="markup-text-input"
                value={textInput.value}
                onChange={(e) => setTextInput((ti) => ({ ...ti, value: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") commitText(); if (e.key === "Escape") cancelTextEdit(); }}
                onTouchEnd={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
                className="bg-transparent text-white text-sm px-2 py-1 outline-none border-b border-white/40 w-40"
                placeholder="Text..."
              />
              <Button size="icon" data-testid="markup-text-commit" onClick={commitText} className="h-8 w-8 bg-primary">
                <Check className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Tool bar */}
      <div className="bg-black/60 px-3 py-2 space-y-2">
        <div className="flex items-center justify-center gap-1 flex-wrap">
          {tools.map((tl) => (
            <button
              key={tl.key}
              data-testid={`markup-tool-${tl.key}`}
              onClick={() => setTool(tl.key)}
              className={`h-11 w-11 rounded-lg flex items-center justify-center transition-colors ${
                tool === tl.key ? "bg-primary text-white" : "text-white/70 hover:bg-white/10"
              }`}
            >
              <tl.icon className="h-5 w-5" />
            </button>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2" data-testid="markup-color-picker">
          {COLORS.map((c) => (
            <button
              key={c}
              data-testid={`markup-color-${c.replace("#", "")}`}
              onClick={() => setColor(c)}
              className={`h-8 w-8 rounded-full border-2 transition-transform ${color === c ? "border-white scale-110" : "border-white/30"}`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        {tool === "text" && <p className="text-center text-white/60 text-xs">{t("add_text_hint")}</p>}
      </div>
    </div>,
    document.body
  );
}
